'use client';

import OraganogenesisLogo from '../../../../../../../public/svg/ogn-logo.svg';
import HamburgerMenu from '../../../../../../../public/svg/hamburger-menu.svg';
import CloseMenu from '../../../../../../../public/svg/close.svg';
import { FC, useState } from 'react';
import { Button, Tooltip } from '../../../../components';
import { motion, AnimatePresence } from 'framer-motion';
import { AppRoute } from '../../../../../enums/enums';
import { appLinkToReadableMap } from '../../../../../maps/maps';
import { cn } from '@shared/shared-utils';
import Link from 'next/link';

type Properties = {
  isMainPage?: boolean;
};

const panelId = 'mobile-nav-panel';

const MobileNavigation: FC<Properties> = ({ isMainPage = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleToggleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu();
    }
  };

  return (
    <nav
      className={cn(
        'relative lg:hidden w-full bg-[linear-gradient(270deg,_#002855_3%,_#041C2C_97.4%)]',
        {
          'lg:block 2xl:hidden': isMainPage,
        },
      )}
      aria-label="Mobile"
    >
      <div className="w-full flex justify-between items-center p-4">
        <div className="relative">
          {isOpen ? (
            <CloseMenu
              className="absolute -bottom-3 w-[28px] h-[26px] cursor-pointer"
              onClick={toggleMenu}
              role="button"
              tabIndex={0}
              aria-label="Close menu"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onKeyDown={handleToggleKeyDown}
            />
          ) : (
            <HamburgerMenu
              className="absolute w-[34px] h-[16px] cursor-pointer -bottom-2"
              onClick={toggleMenu}
              role="button"
              tabIndex={0}
              aria-label="Open menu"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onKeyDown={handleToggleKeyDown}
            />
          )}
        </div>
        <Link href={AppRoute.MAIN} aria-label="Main Page Link">
          <OraganogenesisLogo
            className="w-[232px] h-[22px] text-ogn-bright-yellow"
            aria-hidden="true"
            focusable="false"
          />
        </Link>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={panelId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: 0.5,
              ease: [0.25, 0.46, 0.45, 0.94],
              maxHeight: { duration: 0.5 },
              opacity: { duration: 0.3, delay: 0.1 },
            }}
            className="absolute w-full z-40 overflow-hidden"
            style={{
              background:
                'radial-gradient(92.26% 63.44% at -15.14% 57.06%, #002855 0%, #041C2C 100%)',
            }}
          >
            <div className="py-14 space-y-14">
              <ul className="space-y-14 font-roboto-condensed text-[22px] text-ogn-white font-bold mx-auto w-max text-left">
                {Object.values(AppRoute)
                  .filter((route) => route !== AppRoute.MAIN)
                  .map((route) => (
                    <li onClick={toggleMenu} key={route}>
                      <Link href={route}>
                        {
                          appLinkToReadableMap[
                            route as keyof typeof appLinkToReadableMap
                          ]
                        }
                      </Link>
                    </li>
                  ))}
              </ul>
              <div className="flex justify-center">
                <Tooltip content="Get healing help">
                  <Button
                    to={AppRoute.GET_UPDATES}
                    variant="yellow"
                    className="text-[20px] w-[196px] rounded-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                    onClick={toggleMenu}
                    aria-label="Get healing help"
                  >
                    Get Healing Help
                  </Button>
                </Tooltip>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export { MobileNavigation };
